/**
 * GuestBookingPage - ONE PAGE booking for guests
 * Adapted from customer-portal CompactBookingPage
 *
 * All selections visible at once:
 * 1. Barber tile
 * 2. Service tile (multi-select)
 * 3. Date/Time tile
 * 4. Guest info (name, phone, email)
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { bookingService } from '../services/bookingService';
import { publicHoursService } from '../services/publicHoursService';
import { waitlistService } from '../services/waitlistService';
import NoShowAgreementModal from '../components/NoShowAgreementModal';
import PrepaymentModal from '../components/PrepaymentModal';
import BookingTour from '../components/BookingTour';
import AddToCalendar from '../components/AddToCalendar';
import { parseLocalStart } from '../utils/addToCalendar';
import flatServiceDataService from '../services/flatServiceDataService';
import {
  ArrowLeft,
  Check,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  User,
  Phone,
  Mail,
  Scissors,
  AlertCircle,
  Clock,
  Bell,
  X,
  Lock,
} from 'lucide-react';
import { createAccountFromBooking } from '../services/accountFromBooking';
import { BookingClosedNotice, isBookingOpen } from '../components/BookingClosedNotice';
import toast from 'react-hot-toast';
import { ymd } from '../shared/dateUtils';
import { isBookableBarber } from '../shared/barberStatus';
import { isBarberWorkingOn } from '../shared/availability';
import { resolveServiceDuration, resolveServicePrice } from '../shared/serviceDuration';

// Types
interface Barber {
  id: string;
  firstName: string;
  lastName: string;
  photo?: string;
  // Per-weekday schedule (Jose 2026-09-07): drives the calendar off-day gray-out so a guest
  // can't pick a day the chosen barber isn't in. Same source the staff calendar reads.
  availability?: Record<string, any> | null;
}

interface Service {
  id: string;
  name: string;
  price: number;
  duration: number;
  category: string;
  imageUrl?: string;
}

interface GuestInfo {
  name: string;
  phone: string;
  email: string;
  notes?: string;
}

// Barber Tile Component
const BarberTile: React.FC<{
  barbers: Barber[];
  selectedId: string | null;
  onSelect: (id: string, name: string) => void;
  loading: boolean;
  isActive: boolean;
}> = ({ barbers, selectedId, onSelect, loading, isActive }) => {
  const gradients = [
    'from-purple-500 to-indigo-600',
    'from-blue-500 to-cyan-500',
    'from-emerald-500 to-teal-500',
    'from-orange-500 to-red-500',
    'from-pink-500 to-rose-500',
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-card p-4">
        <h3 className="font-bold text-slik-navy mb-3 flex items-center gap-2">
          <User className="w-5 h-5 text-slik-purple" />
          Choose Your Barber
        </h3>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex-shrink-0 w-20 animate-pulse">
              <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-2" />
              <div className="h-3 bg-gray-200 rounded w-12 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-card p-4 transition-all duration-300 ${isActive ? 'ring-2 ring-slik-purple ring-offset-2' : ''}`}>
      <h3 className="font-bold text-slik-navy mb-1 flex items-center gap-2">
        <User className="w-5 h-5 text-slik-purple" />
        Choose Your Barber
      </h3>
      {isActive && !selectedId && (
        <p className="text-xs text-slik-purple mb-2 animate-pulse">Start by selecting your barber</p>
      )}
      <div className="flex flex-wrap justify-center gap-3 pb-2">
        {barbers.map((barber, idx) => {
          const isSelected = selectedId === barber.id;
          const name = `${barber.firstName} ${barber.lastName}`.trim();
          return (
            <button
              key={barber.id}
              onClick={() => onSelect(barber.id, name)}
              className={`flex-shrink-0 w-24 text-center transition-all ${
                isSelected ? 'scale-105' : 'hover:scale-102'
              }`}
            >
              <div className={`relative w-24 h-24 mx-auto mb-2 rounded-full overflow-hidden ${
                isSelected ? 'ring-3 ring-slik-purple ring-offset-2' : ''
              }`}>
                {barber.photo ? (
                  <img
                    src={barber.photo}
                    alt={name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${gradients[idx % gradients.length]} flex items-center justify-center text-white font-bold text-3xl`}>
                    {barber.firstName?.charAt(0) || 'B'}
                  </div>
                )}
                {isSelected && (
                  <div className="absolute inset-0 bg-slik-purple/20 flex items-center justify-center">
                    <Check className="w-8 h-8 text-white drop-shadow-lg" />
                  </div>
                )}
              </div>
              <p className={`text-sm font-medium truncate ${isSelected ? 'text-slik-purple' : 'text-gray-700'}`}>
                {barber.firstName}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Service Tile Component - With Category Grouping
const ServiceTile: React.FC<{
  services: Service[];
  selectedIds: string[];
  onToggle: (service: Service) => void;
  loading: boolean;
  isActive: boolean;
}> = ({ services, selectedIds, onToggle, loading, isActive }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Group services by category
  const groupedServices = React.useMemo(() => {
    const groups: Record<string, Service[]> = {};
    services.forEach(service => {
      const category = service.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(service);
    });
    return groups;
  }, [services]);

  // Categories default CLOSED (Jose 2026-08-26) — matches the Book Now / customer-portal
  // standard: a compact list, tap to expand, select collapses again. (No auto-expand.)

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-card p-4">
        <h3 className="font-bold text-slik-navy mb-3 flex items-center gap-2">
          <Scissors className="w-5 h-5 text-slik-purple" />
          Select Service(s)
        </h3>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-card p-4 transition-all duration-300 ${isActive ? 'ring-2 ring-slik-purple ring-offset-2' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-slik-navy flex items-center gap-2">
          <Scissors className="w-5 h-5 text-slik-purple" />
          Select Service(s)
        </h3>
        {selectedIds.length > 0 && (
          <span className="text-xs bg-slik-purple text-white px-2 py-1 rounded-full">
            {selectedIds.length} selected
          </span>
        )}
      </div>
      {isActive && selectedIds.length === 0 && (
        <p className="text-xs text-slik-purple mb-2 animate-pulse">Now select your service(s)</p>
      )}

      {/* Category Groups */}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {Object.entries(groupedServices).map(([category, categoryServices]) => {
          const isExpanded = expandedCategories.has(category);
          const selectedInCategory = categoryServices.filter(s => selectedIds.includes(s.id)).length;

          return (
            <div key={category} className="border border-gray-200 rounded-xl overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className={`transition-transform duration-200 ${!isExpanded ? 'animate-pulse' : ''}`}>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slik-purple" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slik-purple" />
                    )}
                  </div>
                  <span className="font-semibold text-gray-800">{category}</span>
                  <span className="text-xs text-gray-500">({categoryServices.length})</span>
                  {!isExpanded && (
                    <span className="text-[10px] text-slik-purple font-medium animate-pulse">tap to expand</span>
                  )}
                </div>
                {selectedInCategory > 0 && (
                  <span className="text-xs bg-slik-purple/20 text-slik-purple px-2 py-0.5 rounded-full font-medium">
                    {selectedInCategory} selected
                  </span>
                )}
              </button>

              {/* Services in Category */}
              {isExpanded && (
                <div className="divide-y divide-gray-100">
                  {categoryServices.map(service => {
                    const isSelected = selectedIds.includes(service.id);
                    return (
                      <button
                        key={service.id}
                        onClick={() => {
                          const wasSelected = selectedIds.includes(service.id);
                          onToggle(service);
                          // Selecting collapses categories so the list stays compact. (Jose 2026-08-26)
                          if (!wasSelected) setExpandedCategories(new Set());
                        }}
                        className={`w-full p-3 flex items-center gap-3 transition-all ${
                          isSelected
                            ? 'bg-slik-purple/10'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-slik-purple text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {service.imageUrl ? (
                            <img src={service.imageUrl} alt={service.name} className="w-full h-full object-cover rounded-lg" />
                          ) : isSelected ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <Scissors className="w-5 h-5" />
                          )}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className={`font-medium truncate ${isSelected ? 'text-slik-purple' : 'text-slik-navy'}`}>
                            {service.name}
                          </p>
                          <p className="text-xs text-gray-500">{service.duration} min</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`font-bold ${isSelected ? 'text-slik-purple' : 'text-gray-900'}`}>
                            ${service.price}
                          </span>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                            isSelected ? 'bg-slik-purple border-slik-purple' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper to format date as YYYY-MM-DD in local timezone
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get today's date string
const getTodayString = (): string => formatLocalDate(new Date());

// Mini Calendar Component - Full Month View
const MiniCalendar: React.FC<{
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  availability?: Record<string, any> | null;
}> = ({ selectedDate, onDateSelect, availability }) => {
  const todayStr = getTodayString();
  const today = new Date();

  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  // SHOP CLOSED (Jose 2026-11-02): warm the hours cache so closed days grey out + the banner shows on load.
  const [, setHoursVer] = useState(0);
  useEffect(() => { publicHoursService.getBusinessHours().then(() => setHoursVer((v) => v + 1)).catch(() => {}); }, []);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get days in month
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const daysInMonth = getDaysInMonth(viewMonth, viewYear);
  const firstDay = getFirstDayOfMonth(viewMonth, viewYear);

  // Generate calendar grid
  const calendarDays = [];

  // Add empty cells for days before the 1st
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }

  // Add actual days
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(viewYear, viewMonth, day);
    const dateStr = formatLocalDate(date);
    // Off = the chosen barber isn't scheduled this weekday (gray it out, not bookable).
    // Only applies once a barber is chosen (availability present). (Jose 2026-09-07)
    // SHOP CLOSED (Jose 2026-11-02): a day the SHOP is closed (weekly OR one-off/holiday) greys out too.
    const closed = publicHoursService.isClosedOn(date);
    const isOff = closed || (!!availability && !isBarberWorkingOn({ availability }, date));
    calendarDays.push({
      day,
      dateStr,
      isPast: dateStr < todayStr,
      isToday: dateStr === todayStr,
      isSelected: dateStr === selectedDate,
      isOff,
      closed,
    });
  }

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // Don't allow navigating to past months
  const canGoPrev = viewYear > today.getFullYear() ||
    (viewYear === today.getFullYear() && viewMonth > today.getMonth());

  return (
    <div className="bg-gray-50 rounded-xl p-3">
      {/* Month/Year Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className="p-1.5 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="font-semibold text-slik-navy">
          {monthNames[viewMonth]} {viewYear}
        </span>
        <button
          onClick={goToNextMonth}
          className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-400 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* SHOP CLOSED banner (Jose 2026-11-02) — days-off / snowstorm close reaches the guest booker here. */}
      {(() => {
        const target = (selectedDate && publicHoursService.isClosedOn(new Date(selectedDate + 'T12:00:00'))) ? selectedDate
          : (publicHoursService.isClosedOn(new Date(todayStr + 'T12:00:00')) ? todayStr : null);
        if (!target) return null;
        const d = new Date(target + 'T12:00:00');
        const note = publicHoursService.specialNote(d);
        const dow = d.toLocaleDateString('en-US', { weekday: 'long' });
        return (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 flex items-start gap-2">
            <span>🗓️</span>
            <p className="text-sm leading-snug">
              <span className="font-semibold text-red-700">We're closed {target === todayStr ? 'today' : `this ${dow}`}.</span>{' '}
              <span className="text-red-600">{note || 'Please pick another day — thanks!'}</span>
            </p>
          </div>
        );
      })()}

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((dayInfo, idx) => {
          if (!dayInfo) {
            return <div key={`empty-${idx}`} className="h-9" />;
          }

          return (
            <button
              key={dayInfo.dateStr}
              type="button"
              onClick={() => !dayInfo.isPast && !dayInfo.isOff && onDateSelect(dayInfo.dateStr)}
              disabled={dayInfo.isPast || dayInfo.isOff}
              title={dayInfo.closed ? 'Shop is closed this day' : dayInfo.isOff ? 'Barber is off this day' : undefined}
              className={`h-9 rounded-lg text-sm font-medium transition-all ${
                dayInfo.isSelected
                  ? 'bg-slik-purple text-white'
                  : dayInfo.isOff
                  ? 'text-gray-300 bg-gray-100 cursor-not-allowed line-through decoration-gray-300'
                  : dayInfo.isToday
                  ? 'bg-slik-green/20 text-slik-green font-bold hover:bg-slik-green/30'
                  : dayInfo.isPast
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-slik-purple/10 hover:text-slik-purple'
              }`}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Date/Time Tile Component
const DateTimeTile: React.FC<{
  barberId: string | null;
  barberAvailability?: Record<string, any> | null;
  selectedDate: string | null;
  selectedTime: string | null;
  onDateSelect: (date: string) => void;
  onTimeSelect: (time: string) => void;
  onJoinWaitlist?: () => void;
  totalDuration: number;
  isActive: boolean;
}> = ({ barberId, barberAvailability, selectedDate, selectedTime, onDateSelect, onTimeSelect, onJoinWaitlist, totalDuration, isActive }) => {
  const [slots, setSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Load time slots when date/barber changes
  useEffect(() => {
    if (!barberId || !selectedDate) {
      setSlots([]);
      return;
    }

    const loadSlots = async () => {
      setLoadingSlots(true);
      try {
        // Query Firestore for available slots (filters out already booked appointments)
        const availableSlots = await bookingService.getAvailableSlots(barberId, selectedDate, undefined, totalDuration);

        // Filter out past times if booking for today
        const now = new Date();
        const today = ymd(now);

        if (selectedDate === today) {
          const currentMinutes = now.getHours() * 60 + now.getMinutes();
          const filteredSlots = availableSlots.filter(slot => {
            // Parse time like "9:00 AM" or "10:30 AM"
            const match = slot.match(/(\d+):(\d+)\s*(AM|PM)/i);
            if (!match) return true;

            let hours = parseInt(match[1]);
            const minutes = parseInt(match[2]);
            const isPM = match[3].toUpperCase() === 'PM';

            if (isPM && hours !== 12) hours += 12;
            if (!isPM && hours === 12) hours = 0;

            const slotMinutes = hours * 60 + minutes;
            return slotMinutes > currentMinutes + 30; // At least 30 min from now
          });
          setSlots(filteredSlots);
        } else {
          setSlots(availableSlots);
        }
      } catch (error) {
        console.error('Error loading slots:', error);
        toast.error('Failed to load available times');
      } finally {
        setLoadingSlots(false);
      }
    };

    loadSlots();
  }, [barberId, selectedDate, totalDuration]);

  const formatTime = (time: string) => {
    // If already in 12-hour format (contains AM/PM), return as-is
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  return (
    <div className={`bg-white rounded-2xl shadow-card p-4 transition-all duration-300 ${isActive ? 'ring-2 ring-slik-purple ring-offset-2' : ''}`}>
      <h3 className="font-bold text-slik-navy mb-1 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-slik-purple" />
        Pick Date & Time
      </h3>
      {isActive && !selectedDate && barberId && (
        <p className="text-xs text-slik-purple mb-2 animate-pulse">Pick a date and time</p>
      )}

      {/* Mini Calendar */}
      <MiniCalendar
        selectedDate={selectedDate}
        onDateSelect={onDateSelect}
        availability={barberAvailability}
      />

      {/* Selected Date Display */}
      {selectedDate && (
        <div className="mt-3 text-center">
          <p className="text-sm text-gray-500">Selected:</p>
          <p className="font-semibold text-slik-purple">
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      )}

      {/* Time Slots */}
      {selectedDate && barberId && (
        <div>
          <p className="text-sm text-gray-500 mb-2">Available times:</p>
          {loadingSlots ? (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="w-20 h-10 bg-gray-100 rounded-lg animate-pulse flex-shrink-0" />
              ))}
            </div>
          ) : slots.length > 0 ? (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {slots.map(time => {
                const isSelected = selectedTime === time;
                return (
                  <button
                    key={time}
                    onClick={() => onTimeSelect(time)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-slik-purple text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {formatTime(time)}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 mb-3">No available times for this date</p>
              <button
                type="button"
                onClick={() => onJoinWaitlist?.()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
              >
                <Bell className="w-4 h-4" />
                Join Waitlist
              </button>
              <p className="text-xs text-gray-400 mt-2">We'll notify you when a slot opens</p>
            </div>
          )}
        </div>
      )}

      {!barberId && (
        <p className="text-center text-gray-400 py-4 text-sm">Select a barber first</p>
      )}
    </div>
  );
};

// Guest Info Tile Component
const GuestInfoTile: React.FC<{
  info: GuestInfo;
  onChange: (info: GuestInfo) => void;
  errors: { name?: string; phone?: string; email?: string };
  isActive: boolean;
}> = ({ info, onChange, errors, isActive }) => {
  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return digits;
  };

  return (
    <div className={`bg-white rounded-2xl shadow-card p-4 transition-all duration-300 ${isActive ? 'ring-2 ring-slik-purple ring-offset-2' : ''}`}>
      <h3 className="font-bold text-slik-navy mb-1 flex items-center gap-2">
        <User className="w-5 h-5 text-slik-purple" />
        Your Information
      </h3>
      {isActive && !info.name && (
        <p className="text-xs text-slik-purple mb-2 animate-pulse">Enter your contact details</p>
      )}
      <div className="space-y-3">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={info.name}
            onChange={(e) => onChange({ ...info, name: e.target.value })}
            placeholder="Your name"
            className={`w-full p-3 rounded-xl border ${
              errors.name ? 'border-red-500' : 'border-gray-200'
            } focus:border-slik-purple focus:ring-2 focus:ring-slik-purple/20 outline-none`}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              value={formatPhone(info.phone)}
              onChange={(e) => onChange({ ...info, phone: e.target.value })}
              placeholder="(555) 555-5555"
              className={`w-full p-3 pl-10 rounded-xl border ${
                errors.phone ? 'border-red-500' : 'border-gray-200'
              } focus:border-slik-purple focus:ring-2 focus:ring-slik-purple/20 outline-none`}
            />
          </div>
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={info.email}
              onChange={(e) => onChange({ ...info, email: e.target.value })}
              placeholder="you@example.com"
              autoComplete="email"
              inputMode="email"
              className={`w-full p-3 pl-10 rounded-xl border ${
                errors.email ? 'border-red-500' : 'border-gray-200'
              } focus:border-slik-purple focus:ring-2 focus:ring-slik-purple/20 outline-none`}
            />
          </div>
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={info.notes || ''}
            onChange={(e) => onChange({ ...info, notes: e.target.value })}
            placeholder="Any special requests?"
            rows={2}
            className="w-full p-3 rounded-xl border border-gray-200 focus:border-slik-purple focus:ring-2 focus:ring-slik-purple/20 outline-none resize-none"
          />
        </div>
      </div>
    </div>
  );
};

// Main Component
const GuestBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Live blinking walkthrough (/book?tour=1) — the Way-2 web tour from the /app launch page.
  const tourActive = searchParams.get('tour') === '1';

  // URL params for pre-selection
  const preSelectedServiceId = searchParams.get('serviceId');
  const preSelectedBarberId = searchParams.get('barberId');

  // Data state
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedBarberId, setSelectedBarberId] = useState<string | null>(null);
  const [selectedBarberName, setSelectedBarberName] = useState<string>('');
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({ name: '', phone: '', email: '' });
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string }>({});

  // Booking state
  const [submitting, setSubmitting] = useState(false);
  // Explicit SMS opt-in checkbox (Twilio TFV 30513 — affirmative consent proof). Optional (consent
  // is not a condition of booking); when checked we capture smsConsent on the appointment. (2026-09-25)
  const [smsConsent, setSmsConsent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  // Book Now → account (Jose 2026-10-12): the offer pops the moment they start their info; opting in
  // reveals a password; "No thanks" books as a guest. Account creation NEVER blocks the booking.
  const [acctChoice, setAcctChoice] = useState<'pending' | 'yes' | 'no'>('pending');
  const [acctPassword, setAcctPassword] = useState('');
  const [acctErr, setAcctErr] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);
  // No-show block agreement gate (2026-08-07)
  const [blockGate, setBlockGate] = useState<{ notice: string; apptData: any; serviceLabel: string; priceLabel: string; clientName: string } | null>(null);
  // REAL upfront charge for a no-show-blocked guest (Jose 2026-10-25) — the PrepaymentModal path.
  const [prepayGate, setPrepayGate] = useState<{ notice: string; apptData: any; serviceName: string; amount: number; clientEmail: string } | null>(null);
  const [agreeSubmitting, setAgreeSubmitting] = useState(false);

  // Waitlist modal state
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistDays, setWaitlistDays] = useState<string[]>([]);
  const [waitlistTimePreference, setWaitlistTimePreference] = useState<'any' | 'morning' | 'afternoon' | 'evening'>('any');
  const [waitlistTextNotifications, setWaitlistTextNotifications] = useState(true);
  // Waitlist barber preference (Jose 2026-10-31): the modal used to silently lock to the pre-picked
  // barber with no "any barber" option. Defaults to the barber they were viewing; switchable.
  const [waitlistBarberPref, setWaitlistBarberPref] = useState<string>('any');
  useEffect(() => {
    if (showWaitlistModal) setWaitlistBarberPref(selectedBarberId || 'any');
  }, [showWaitlistModal, selectedBarberId]);

  // Scroll to top on page load (important for pre-selections from landing page)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Load data
  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      // Load barbers
      const barbersSnapshot = await getDocs(collection(db, 'barbers'));
      const barbersData = barbersSnapshot.docs
        // BRAIN #4 (2026-08-07): hide archived/inactive/apprentice — matches POS + shared/barberStatus.ts
        .filter(doc => isBookableBarber(doc.data() as any))
        .map(doc => ({
          id: doc.id,
          firstName: doc.data().firstName || '',
          lastName: doc.data().lastName || '',
          photo: doc.data().photo,
          availability: doc.data().availability,
        }));
      setBarbers(barbersData);

      // Load flat services
      const flatServices = await flatServiceDataService.getFlatServiceList(true);
      const servicesData: Service[] = flatServices.map(s => ({
        id: s.id,
        name: s.name,
        price: s.price,
        duration: s.duration,
        category: s.categoryName,
        imageUrl: s.imageUrl,
      }));

      setServices(servicesData);

      // Apply pre-selections from URL params
      if (preSelectedBarberId) {
        const barber = barbersData.find(b => b.id === preSelectedBarberId);
        if (barber) {
          setSelectedBarberId(barber.id);
          setSelectedBarberName(`${barber.firstName} ${barber.lastName}`.trim());
          toast.success(`Booking with ${barber.firstName}!`);
        }
      }

      if (preSelectedServiceId) {
        const service = servicesData.find(s => s.id === preSelectedServiceId);
        if (service) {
          setSelectedServices([service]);
          toast.success(`${service.name} selected!`);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load booking data');
    } finally {
      setLoading(false);
    }
  };

  const handleBarberSelect = (id: string, name: string) => {
    setSelectedBarberId(id);
    setSelectedBarberName(name);
    setSelectedTime(null); // Reset time when barber changes
  };

  const handleServiceToggle = (service: Service) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === service.id);
      if (exists) {
        return prev.filter(s => s.id !== service.id);
      }
      return [...prev, service];
    });
  };

  // Resolve each service's time/price against the chosen barber's overrides (Services & Times). No-op
  // until a barber is configured (fallback = menu default). (Jose 2026-10-17)
  const _selBarber = barbers.find(b => b.id === selectedBarberId) as any;
  const totalPrice = selectedServices.reduce((sum, s) =>
    sum + resolveServicePrice(s.name, selectedBarberId, { barberPrices: _selBarber?.servicePrices, fallback: s.price }), 0);
  const totalDuration = selectedServices.reduce((sum, s) =>
    sum + resolveServiceDuration(s.name, selectedBarberId, { barberDurations: _selBarber?.serviceDurations, fallback: s.duration }), 0);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // LAST NAME REQUIRED (Jose 2026-11-03): new online clients were booking as ONE name ("Frank") → Diana's
    // board showed a single name with no last name. Require a real first AND last name (last ≥ 2 chars so
    // a bare initial doesn't pass). This is the ONLY point that captures a guest's name.
    const nameParts = guestInfo.name.trim().split(/\s+/).filter(Boolean);
    if (!guestInfo.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (nameParts.length < 2 || nameParts[nameParts.length - 1].length < 2) {
      newErrors.name = 'Please enter your first and last name';
    }

    const phoneDigits = guestInfo.phone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) {
      newErrors.phone = 'Valid phone number is required';
    }

    if (!guestInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestInfo.email)) {
      newErrors.email = 'Valid email is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canBook = selectedBarberId && selectedServices.length > 0 && selectedDate && selectedTime;

  // Guided flow state - determines which tile is active
  const currentStep = !selectedBarberId ? 'barber'
    : selectedServices.length === 0 ? 'service'
    : !selectedDate || !selectedTime ? 'datetime'
    : guestInfo.name.trim().split(/\s+/).filter(Boolean).length < 2 || !guestInfo.phone || !guestInfo.email ? 'info'
    : 'ready';

  const handleBooking = async () => {
    if (!canBook) return;
    if (!validateForm()) return;
    // If they opted to create an account, the password must be valid before we book.
    if (acctChoice === 'yes' && acctPassword.trim().length < 6) {
      setAcctErr('Password must be at least 6 characters');
      return;
    }
    setAcctErr('');

    setSubmitting(true);

    // OPTIONAL account creation — NEVER blocks the booking. Links the anon session into a real account so
    // the appointment they're about to make is owned by it (same login as the app). (Jose 2026-10-12)
    let madeAccount = false;
    if (acctChoice === 'yes' && acctPassword.trim().length >= 6 && guestInfo.email) {
      try {
        const r = await createAccountFromBooking(guestInfo.email, acctPassword.trim(), guestInfo.name, guestInfo.phone);
        madeAccount = r.created;
        if (!r.created && r.reason === 'exists-wrong-password') {
          toast('You already have an account — booking you as a guest. Log in to see it.', { icon: 'ℹ️' });
        }
      } catch { /* non-fatal — proceed as guest */ }
    }
    setAccountCreated(madeAccount);

    // Built before the call so the no-show-block gate can retry it with the agreement attached.
    const appointment: any = {
      clientId: auth.currentUser?.uid || 'guest_' + Date.now(),
      clientName: guestInfo.name,
      clientPhone: guestInfo.phone.replace(/\D/g, ''),
      clientEmail: guestInfo.email,
      barberId: selectedBarberId,
      barberName: selectedBarberName,
      service: selectedServices.map(s => s.name).join(', '),
      date: selectedDate,
      time: selectedTime,
      duration: totalDuration,
      price: totalPrice,
      status: 'confirmed',
      source: 'online' as const,
      reminderSent: false,
      isGuest: !madeAccount,
      // Explicit SMS opt-in capture (Twilio TFV proof of consent). (2026-09-25)
      smsConsent: smsConsent,
      smsConsentAt: smsConsent ? new Date().toISOString() : null,
      notes: guestInfo.notes || '',
    };

    try {
      // Route through the service so guests get the NO-SHOW BLOCK + new-client detection
      // (was a raw addDoc that bypassed both). shared/noShowBlock.ts
      const created = await bookingService.createAppointment(appointment);
      setBookingId(created.id || null);
      setSuccess(true);
      toast.success('Appointment booked successfully!');
    } catch (error: any) {
      if (error?.code === 'NO_SHOW_BLOCK') {
        // Blocked client → REAL prepay charge (Jose 2026-10-25). Open the Stripe PrepaymentModal; on a
        // confirmed charge we re-book with the payment proof so the server gate lets it through.
        setPrepayGate({ notice: error.message, apptData: appointment, serviceName: appointment.service, amount: Number(totalPrice) || 0, clientEmail: appointment.clientEmail || '' });
      } else if (error?.code === 'permission-denied') {
        toast.error('Session expired. Please refresh the page and try again.');
      } else {
        console.error('Booking error:', error);
        // Surface the REAL reason (e.g. "not enough time for X at 10:30…") instead of a blank
        // "Failed to book" — clients were hitting a dead-end message and texting Need-Help. (2026-10-22)
        toast.error(error?.message || 'Could not book that time. Please pick another slot.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Retry the guest booking once the blocked client PAYS in full (real Stripe charge). (Jose 2026-10-25)
  const submitPrepaidCharge = async (paymentIntentId: string) => {
    if (!prepayGate) return;
    try {
      const created = await bookingService.createAppointment({ ...prepayGate.apptData, prepaidPaymentIntentId: paymentIntentId });
      setPrepayGate(null);
      setBookingId(created.id || null);
      setSuccess(true);
      toast.success('Paid & booked — see you then! ✂️');
    } catch (e: any) {
      // The charge cleared but the booking write failed — don't lose the money silently.
      toast.error(e?.message || 'Payment went through but the booking didn’t save — please call us and we’ll sort it out.', { duration: 9000 });
    }
  };

  // Retry the guest booking once the blocked client signs the prepay agreement. (legacy e-sign path)
  const submitPrepaid = async (agreement: any) => {
    if (!blockGate) return;
    setAgreeSubmitting(true);
    try {
      const created = await bookingService.createAppointment({ ...blockGate.apptData, prepaidAgreement: agreement });
      setBlockGate(null);
      setBookingId(created.id || null);
      setSuccess(true);
      toast.success('Appointment booked — payment due at your visit.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not book. Please try again.');
    } finally {
      setAgreeSubmitting(false);
    }
  };

  // PRE-LAUNCH GATE (Jose 2026-10-13): SLIK booking opens Oct 19. Until then the shop runs on Fresha, so
  // a SLIK booking would be orphaned — show the "keep booking on Fresha" notice instead of the picker.
  if (!isBookingOpen()) {
    return <BookingClosedNotice />;
  }

  // Success Screen
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-card p-6 max-w-md w-full text-center animate-scale-in">
          <div className="w-20 h-20 bg-slik-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-slik-green" />
          </div>
          <h1 className="text-2xl font-bold text-slik-navy mb-2">Booking Confirmed!</h1>
          <p className="text-gray-600 mb-6">
            We'll send a confirmation to <span className="font-medium">{guestInfo.email}</span>
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="font-medium">{new Date(selectedDate! + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Time</span>
                <span className="font-medium">{selectedTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Barber</span>
                <span className="font-medium">{selectedBarberName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Services</span>
                <span className="font-medium text-right">{selectedServices.map(s => s.name).join(', ')}</span>
              </div>
              <div className="border-t pt-2 mt-2 flex justify-between">
                <span className="font-bold">Total</span>
                <span className="font-bold text-slik-purple">${totalPrice}</span>
              </div>
            </div>
          </div>

          {selectedDate && selectedTime && (
            <div className="mb-4">
              <AddToCalendar event={{
                title: `SLIK Barber Lounge — ${selectedServices.map(s => s.name).join(', ')}`,
                start: parseLocalStart(selectedDate, selectedTime),
                durationMin: totalDuration,
                location: 'SLIK Barber Lounge',
                details: `${selectedServices.map(s => s.name).join(', ')} with ${selectedBarberName}. Manage or reschedule: https://my.slikbarberlounge.com`,
              }} />
            </div>
          )}

          {accountCreated && (
            <div className="bg-slik-purple/5 border border-slik-purple/20 rounded-xl p-3 mb-4 text-sm text-slik-navy text-left">
              ✅ <span className="font-semibold">Your SLIK account is ready.</span> Log in anytime at{' '}
              <span className="font-semibold">my.slikbarberlounge.com</span> — and in the SLIK app — with your email &amp; password.
            </div>
          )}

          <div className="space-y-3">
            {accountCreated ? (
              <a
                href="https://my.slikbarberlounge.com"
                className="block w-full py-3 bg-slik-purple text-white rounded-button font-bold hover:bg-slik-purple/90 transition-colors"
              >
                Go to my account
              </a>
            ) : (
              <Link
                to="/"
                className="block w-full py-3 bg-slik-purple text-white rounded-button font-bold hover:bg-slik-purple/90 transition-colors"
              >
                Back to Home
              </Link>
            )}
            {!accountCreated && (
              <p className="text-sm text-gray-500">
                Already a client?{' '}
                <Link to="/login" className="text-slik-purple font-medium hover:underline">
                  Login to your portal
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <h1 className="text-lg font-bold text-slik-navy">Book Appointment</h1>
          {/* Persistent JOIN-THE-WAITLIST button (Jose 2026-08-09): ALWAYS visible for ANY day —
              NOT gated by barber/service/date/availability (the FAB below only showed with no service
              selected). Sticky header = never scrolls off. Opens the existing waitlist modal. */}
          <button
            type="button"
            onClick={() => setShowWaitlistModal(true)}
            className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-sm font-semibold hover:bg-purple-100 transition-colors"
          >
            <Bell className="w-4 h-4" />
            Waitlist
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Barber Tile */}
        <div data-tour="barber">
          <BarberTile
            barbers={barbers}
            selectedId={selectedBarberId}
            onSelect={handleBarberSelect}
            loading={loading}
            isActive={currentStep === 'barber'}
          />
        </div>

        {/* Service Tile */}
        <div data-tour="service">
          <ServiceTile
            services={services}
            selectedIds={selectedServices.map(s => s.id)}
            onToggle={handleServiceToggle}
            loading={loading}
            isActive={currentStep === 'service'}
          />
        </div>

        {/* Date/Time Tile */}
        <div data-tour="datetime">
          <DateTimeTile
            barberId={selectedBarberId}
            barberAvailability={barbers.find(b => b.id === selectedBarberId)?.availability}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateSelect={setSelectedDate}
            onTimeSelect={setSelectedTime}
            onJoinWaitlist={() => setShowWaitlistModal(true)}
            totalDuration={totalDuration}
            isActive={currentStep === 'datetime'}
          />
        </div>

        {/* Guest Info Tile */}
        <div data-tour="info">
          <GuestInfoTile
            info={guestInfo}
            onChange={setGuestInfo}
            errors={errors}
            isActive={currentStep === 'info'}
          />
        </div>

        {/* Book Now → account: pops the moment they start their info. Opt in → a password slides in;
            "No thanks" → straight to booking as a guest. Same info, no re-typing. (Jose 2026-10-12) */}
        {guestInfo.name.trim() && acctChoice !== 'no' && (
          <div className="bg-white rounded-2xl shadow-card p-4 border border-slik-purple/20 animate-scale-in">
            {acctChoice === 'pending' && (
              <>
                <h3 className="font-bold text-slik-navy flex items-center gap-2">
                  <User className="w-5 h-5 text-slik-purple" /> Save your info — create a SLIK account
                </h3>
                <p className="text-sm text-gray-500 mt-1">Manage your bookings, get reminders &amp; earn rewards — and the same login works in the SLIK app.</p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => setAcctChoice('yes')} className="flex-1 bg-slik-purple text-white py-2.5 rounded-button font-bold shadow-button hover:opacity-90 transition-all">Yes, create my account</button>
                  <button type="button" onClick={() => { setAcctChoice('no'); setAcctPassword(''); setAcctErr(''); }} className="px-4 py-2.5 rounded-button bg-gray-100 text-gray-600 font-medium">No thanks</button>
                </div>
              </>
            )}
            {acctChoice === 'yes' && (
              <form onSubmit={(e) => e.preventDefault()}>
                <h3 className="font-bold text-slik-navy flex items-center gap-2">
                  <Lock className="w-5 h-5 text-slik-purple" /> Create your password
                </h3>
                <p className="text-sm text-gray-500 mt-1">You're all set with <span className="font-medium">{guestInfo.email || 'your email'}</span> — just pick a password.</p>
                {/* Hidden username so iOS "Use Strong Password" + Chrome pair the credential with their
                    email and SAVE it to Keychain/Google automatically when they set the password. (Jose 2026-10-13) */}
                <input type="text" name="username" autoComplete="username" value={guestInfo.email} readOnly tabIndex={-1} aria-hidden="true" className="sr-only" />
                <input
                  type="password"
                  value={acctPassword}
                  onChange={(e) => { setAcctPassword(e.target.value); if (acctErr) setAcctErr(''); }}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  className={`w-full mt-3 p-3 rounded-xl border ${acctErr ? 'border-red-500' : 'border-gray-200'} focus:border-slik-purple focus:ring-2 focus:ring-slik-purple/20 outline-none`}
                />
                {acctErr && <p className="text-red-500 text-xs mt-1">{acctErr}</p>}
                {/* SAVE-PASSWORD nudge — people forget + can't log in later. Glowing amber so it's read. (Jose 2026-10-13) */}
                <div className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 border-2 border-amber-400 p-2.5 animate-pulse">
                  <span className="text-base leading-none">💡</span>
                  <p className="text-xs font-bold text-amber-800">Tap <span className="underline">“Save”</span> when your phone offers to remember this password — then logging in later is one tap.</p>
                </div>
                {/* GUIDANCE: once the password is valid, point them DOWN to Confirm. People click, they don't read. */}
                {acctPassword.trim().length >= 6 && (
                  <div className="mt-3 flex flex-col items-center text-slik-purple font-bold text-sm">
                    <span>Almost done — tap “Confirm Booking” below</span>
                    <ChevronDown className="w-7 h-7 animate-bounce mt-0.5" />
                  </div>
                )}
                <button type="button" onClick={() => { setAcctChoice('no'); setAcctPassword(''); setAcctErr(''); }} className="text-xs text-gray-400 mt-2 underline">No thanks, just book as a guest</button>
              </form>
            )}
          </div>
        )}

        {/* data-tour="ready" wraps BOTH the SMS opt-in and the Confirm button so the walkthrough's
            final spotlight includes the (optional) text-consent box — otherwise the ring jumped
            straight from the info tile to Confirm and clients missed it. (Jose 2026-10-08) */}
        <div data-tour="ready" className="space-y-4">
        {/* SMS OPT-IN (Twilio Toll-Free Verification 30513) — ALWAYS VISIBLE at the point of phone
            entry so a carrier reviewer sees the affirmative-consent checkbox without completing a
            booking. Carries every required element: company · frequency · rates · HELP/STOP ·
            Privacy + Terms. Optional (not a condition of booking); captured as smsConsent. (2026-09-25) */}
        <div className="bg-white rounded-2xl shadow-card p-4">
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-slik-purple shrink-0"
            />
            <span className="text-[12px] text-gray-600 leading-relaxed">
              I agree to receive appointment &amp; account SMS messages from{' '}
              <span className="font-semibold">SLIK Barber Lounge</span> at the number provided.
              Message frequency may vary. Msg &amp; data rates may apply. Reply{' '}
              <span className="font-semibold">HELP</span> for help or{' '}
              <span className="font-semibold">STOP</span> to unsubscribe. See our{' '}
              <a href="/privacy" className="text-slik-purple underline">Privacy Policy</a> and{' '}
              <a href="/terms" className="text-slik-purple underline">Terms &amp; Conditions</a>.
              Consent is not a condition of booking.
            </span>
          </label>
        </div>

        {/* Summary & Book Button */}
        {selectedServices.length > 0 && (
          <div className="bg-white rounded-2xl shadow-card p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">{selectedServices.length} service(s)</p>
                <p className="text-xs text-gray-400">{totalDuration} min total</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slik-purple">${totalPrice}</p>
              </div>
            </div>

            <button
              onClick={handleBooking}
              disabled={!canBook || submitting}
              className={`w-full bg-slik-purple text-white py-4 rounded-button font-bold shadow-button hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${currentStep === 'ready' && !submitting ? 'ring-4 ring-slik-purple/40 ring-offset-2 animate-pulse' : ''}`}
            >
              {submitting ? (
                // Reassuring copy (Jose 2026-11-02): name the work so the confirm wait never reads as a hang.
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white" />
                  Locking in your time…
                </span>
              ) : (
                'Confirm Booking'
              )}
            </button>

            {/* Explicit opt-in now lives in the checkbox above (Twilio TFV 30513). This line just keeps
                STOP/HELP + the policy links reachable at the point of action. */}
            <p className="text-[11px] text-gray-400 text-center mt-2 leading-relaxed">
              Texts are sent from SLIK Barber Lounge. Reply <span className="font-semibold">STOP</span> to opt out,{' '}
              <span className="font-semibold">HELP</span> for help. See our{' '}
              <a href="/privacy" className="text-slik-purple underline">Privacy Policy</a> &amp;{' '}
              <a href="/terms" className="text-slik-purple underline">Terms</a>.
            </p>

            {!canBook && (
              <p className="text-center text-gray-400 text-sm mt-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Select barber, service, date & time
              </p>
            )}

            {/* Persistent Waitlist Link */}
            <div className="mt-3 pt-3 border-t border-gray-100 text-center">
              <button
                type="button"
                onClick={() => setShowWaitlistModal(true)}
                className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
              >
                <Bell className="w-4 h-4" />
                Can't find a time? Join the Waitlist
              </button>
            </div>
          </div>
        )}
        </div>{/* /data-tour="ready" wrapper (SMS box + Confirm) */}
      </main>

      {/* Live blinking walkthrough (Way 2 — /book?tour=1). Rides currentStep; blinks the active
          section + shows a bottom coach bar. Only rendered when the tour is on. */}
      <BookingTour step={currentStep} active={tourActive} />

      {/* Persistent Waitlist FAB - Always visible at bottom (hidden during the guided tour so it
          doesn't collide with the coach bar) */}
      {selectedServices.length === 0 && !tourActive && (
        <div className="fixed bottom-4 left-0 right-0 flex justify-center z-30">
          <button
            type="button"
            onClick={() => setShowWaitlistModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-full shadow-lg border border-purple-200 hover:bg-purple-50 transition-all"
          >
            <Bell className="w-5 h-5" />
            <span className="font-semibold">Join Waitlist</span>
          </button>
        </div>
      )}

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Join the Waitlist</h3>
              <button
                onClick={() => setShowWaitlistModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {waitlistSuccess ? (
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">You're on the Waitlist!</h4>
                <p className="text-gray-600 mb-4">
                  We'll text you at <span className="font-medium">{guestInfo.phone}</span> when a slot opens up.
                </p>
                <button
                  onClick={() => {
                    setShowWaitlistModal(false);
                    setWaitlistSuccess(false);
                  }}
                  className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                <p className="text-sm text-gray-600">
                  No available times? Join the waitlist and we'll notify you when a slot opens.
                </p>

                {/* Barber Selection (Jose 2026-10-31): pick ANY barber or a specific one. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Which barber?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setWaitlistBarberPref('any')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        waitlistBarberPref === 'any'
                          ? 'bg-[#f3e3d2] text-[#8a4f1f] ring-1 ring-inset ring-[#c0824a]'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Any barber
                    </button>
                    {barbers.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setWaitlistBarberPref(b.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          waitlistBarberPref === b.id
                            ? 'bg-[#f3e3d2] text-[#8a4f1f] ring-1 ring-inset ring-[#c0824a]'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {b.firstName}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Day Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Which days work for you?
                  </label>
                  <div className="grid grid-cols-7 gap-1">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => {
                      const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][idx];
                      const isSelected = waitlistDays.includes(dayName);
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setWaitlistDays(waitlistDays.filter(d => d !== dayName));
                            } else {
                              setWaitlistDays([...waitlistDays, dayName]);
                            }
                          }}
                          className={`py-2 rounded-lg text-xs font-medium transition-all ${
                            isSelected
                              ? 'bg-[#f3e3d2] text-[#8a4f1f] ring-1 ring-inset ring-[#c0824a]'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Preference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred time
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'any', label: 'Any Time' },
                      { id: 'morning', label: 'Morning (9-12)' },
                      { id: 'afternoon', label: 'Afternoon (12-3)' },
                      { id: 'evening', label: 'Evening (3-6)' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setWaitlistTimePreference(opt.id as any)}
                        className={`p-2 rounded-lg text-sm font-medium transition-all ${
                          waitlistTimePreference === opt.id
                            ? 'bg-[#f3e3d2] text-[#8a4f1f] ring-1 ring-inset ring-[#c0824a]'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* SMS Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium">Text me when available</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setWaitlistTextNotifications(!waitlistTextNotifications)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      waitlistTextNotifications ? 'bg-[#c0824a]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                        waitlistTextNotifications ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Contact Info — inline in modal so user can always fill it */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Your info</label>
                  <input
                    type="text"
                    autoComplete="name"
                    placeholder="First and last name"
                    value={guestInfo.name}
                    onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value, })}
                    className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  {errors.name && <p className="text-xs text-red-500 -mt-1">{errors.name}</p>}
                  <input
                    type="tel"
                    placeholder="Phone number"
                    value={guestInfo.phone}
                    onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Submit */}
                <button
                  onClick={async () => {
                    if (!guestInfo.name || !guestInfo.phone) {
                      toast.error('Please enter your name and phone number first');
                      return;
                    }
                    if (waitlistDays.length === 0) {
                      toast.error('Please select at least one day');
                      return;
                    }

                    setWaitlistSubmitting(true);
                    try {
                      await waitlistService.addToWaitlist({
                        customerName: guestInfo.name,
                        customerPhone: guestInfo.phone,
                        customerEmail: guestInfo.email || undefined,
                        selectedDays: waitlistDays,
                        barberPreference: waitlistBarberPref,
                        barberName: waitlistBarberPref === 'any'
                          ? undefined
                          : (barbers.find((b) => b.id === waitlistBarberPref) ? `${barbers.find((b) => b.id === waitlistBarberPref)!.firstName} ${barbers.find((b) => b.id === waitlistBarberPref)!.lastName}`.trim() : selectedBarberName || undefined),
                        timePreference: waitlistTimePreference,
                        textNotifications: waitlistTextNotifications,
                      });
                      setWaitlistSuccess(true);
                      toast.success("You're on the waitlist!");
                    } catch (error) {
                      console.error('Waitlist error:', error);
                      toast.error('Failed to join waitlist. Please try again.');
                    } finally {
                      setWaitlistSubmitting(false);
                    }
                  }}
                  disabled={waitlistSubmitting || !guestInfo.name || !guestInfo.phone || waitlistDays.length === 0}
                  className="w-full py-3 bg-gradient-to-r from-[#1c1a17] to-[#c0824a] text-white rounded-xl font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-button"
                >
                  {waitlistSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white" />
                      Joining...
                    </span>
                  ) : (
                    'Join Waitlist'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No-show block: REAL prepay charge (primary, Jose 2026-10-25) — pay in full to confirm. */}
      {prepayGate && (
        <PrepaymentModal
          amount={prepayGate.amount}
          serviceName={prepayGate.serviceName}
          clientEmail={prepayGate.clientEmail}
          notice={prepayGate.notice}
          onSuccess={submitPrepaidCharge}
          onClose={() => setPrepayGate(null)}
        />
      )}

      {/* No-show block: legacy prepay agreement / e-signature gate (kept for reference; not triggered). */}
      <NoShowAgreementModal
        open={!!blockGate}
        notice={blockGate?.notice || ''}
        clientName={blockGate?.clientName}
        serviceLabel={blockGate?.serviceLabel}
        priceLabel={blockGate?.priceLabel}
        submitting={agreeSubmitting}
        onClose={() => setBlockGate(null)}
        onAgree={submitPrepaid}
      />
    </div>
  );
};

export default GuestBookingPage;
